import * as THREE from 'three';
import { COP, DUCKLING, DUCKLING_ANIM, DUCKLING_CHIRP } from '../config';
import { createDuckling, type DucklingEntity } from '../entities/duckling';
import { attachHatWithTracking } from '../entities/hats';
import { showPopup } from '../ui/scorePopups';
import type { System, World } from '../types';
import { addChaos } from './chaosSystem';
import { getCops, type CopEntity } from './copSystem';
import { hatForTier, getCurrentTierIndex } from './duckEvolutionSystem';
import { getPathPoint, maybeAddPathPoint } from './ducklingPath';
import { enterRage, exitRage, triggerShake, triggerSlowMo } from './juiceSystem';
import { pushOutOfBuildings } from '../utils/buildingCollision';
import { playDucklingChirp } from '../utils/ducklingAudio';

const ducklings: DucklingEntity[] = [];
let totalHatched = 0, totalKilled = 0, rageUntil = 0, wasRaging = false, nextChirpAt = 0;
export function getTotalHatched(): number { return totalHatched; }
export function getTotalKilled(): number { return totalKilled; }
export function isMotherEnraged(world: World): boolean { return world.time.elapsed < rageUntil; }
export function getRageRemaining(world: World): number { return Math.max(0, rageUntil - world.time.elapsed); }
function triggerMotherRage(world: World): void { rageUntil = world.time.elapsed + DUCKLING.rageDurationSeconds; showPopup(world, 'motherRage'); enterRage(world); }

export function getDucklings(): readonly DucklingEntity[] { return ducklings; }

export function spawnDuckling(world: World): void {
  if (ducklings.length >= DUCKLING.maxBrood) return;
  const d = createDuckling(world, ducklings.length);
  ducklings.push(d);
  totalHatched++;
  showPopup(world, 'ducklingHatch');
  if (ducklings.length === DUCKLING.maxBrood) attachHatWithTracking(world.duck, 'chef');
}

export function recallDucklings(world: World): void {
  const until = world.time.elapsed + DUCKLING.recallSeconds;
  for (const d of ducklings) {
    if (d.mode === 'defending') {
      d.mode = 'returning';
      d.assignedCop = null;
      d.returnUntil = until;
    }
  }
}

function disposeGroup(o: THREE.Object3D): void {
  const m = o as THREE.Mesh;
  if (m.geometry) m.geometry.dispose();
  if (m.material) (Array.isArray(m.material) ? m.material : [m.material]).forEach(mt => mt.dispose());
  o.children.forEach(disposeGroup);
}

export function killDuckling(world: World, d: DucklingEntity): void {
  const idx = ducklings.indexOf(d);
  if (idx === -1) return;
  disposeGroup(d.group);
  world.scene.remove(d.group);
  ducklings.splice(idx, 1);
  totalKilled++;
  for (let i = 0; i < ducklings.length; i++) ducklings[i]!.index = i;
  showPopup(world, 'ducklingDown');
  triggerShake(0.6);
  triggerSlowMo(world);
  addChaos(world, 'ducklingDown');
  triggerMotherRage(world);
  if (ducklings.length < DUCKLING.maxBrood) {
    const tierHat = hatForTier(getCurrentTierIndex());
    if (tierHat) attachHatWithTracking(world.duck, tierHat);
  }
}
const _toCop = new THREE.Vector3();
const _orbitTarget = new THREE.Vector3();
const _push = new THREE.Vector3();
let lastThreatCheck = 0;

function reassignDefenders(world: World): void {
  const mother = world.duck.position;
  const threats = getCops().filter(c => c.group.position.distanceTo(mother) <= DUCKLING.defendRadius);
  if (threats.length === 0) {
    for (const d of ducklings) if (d.mode === 'defending') { d.mode = 'returning'; d.assignedCop = null; d.returnUntil = world.time.elapsed + 0.4; }
    return;
  }
  for (let i = 0; i < ducklings.length; i++) {
    const d = ducklings[i]!;
    if (d.mode === 'returning' && world.time.elapsed < d.returnUntil) continue;
    d.mode = 'defending';
    d.assignedCop = threats[i % threats.length]!;
    d.orbitAngle += 0.05;
  }
}

function updateFollowing(d: DucklingEntity, _dt: number): void {
  const target = getPathPoint(d.index + 1);
  if (!target) return;
  d.group.position.x += (target.x - d.group.position.x) * DUCKLING.followLerp;
  d.group.position.z += (target.z - d.group.position.z) * DUCKLING.followLerp;
  let yawDiff = target.yaw - d.group.rotation.y;
  if (yawDiff > Math.PI) yawDiff -= Math.PI * 2;
  if (yawDiff < -Math.PI) yawDiff += Math.PI * 2;
  d.group.rotation.y += yawDiff * DUCKLING_ANIM.rotationLerp;
}

function updateDefending(d: DucklingEntity, world: World, dt: number): void {
  const copEntity = d.assignedCop;
  if (!copEntity) return;
  if (!getCops().includes(copEntity)) {
    d.assignedCop = null;
    d.mode = 'returning';
    d.returnUntil = world.time.elapsed + 0.4;
    return;
  }
  const cop = copEntity.group;
  d.orbitAngle += 1.2 * dt;
  _orbitTarget.set(
    cop.position.x + Math.cos(d.orbitAngle) * DUCKLING.orbitRadius,
    0,
    cop.position.z + Math.sin(d.orbitAngle) * DUCKLING.orbitRadius,
  );
  _toCop.copy(_orbitTarget).sub(d.group.position);
  _toCop.y = 0;
  const dist = _toCop.length();
  if (dist > 0.01) {
    _toCop.normalize();
    d.group.position.addScaledVector(_toCop, DUCKLING.defendSpeed * dt);
    d.group.rotation.y = Math.atan2(_toCop.x, _toCop.z);
  }
  const distToCop = d.group.position.distanceTo(cop.position);
  if (distToCop < DUCKLING.pushRadius) {
    _push.copy(cop.position).sub(world.duck.position);
    _push.y = 0;
    if (_push.lengthSq() > 1e-4) {
      _push.normalize().multiplyScalar(DUCKLING.pushForce * dt);
      cop.position.add(_push);
      copEntity.pushResist--;
      if (copEntity.pushResist <= 0) {
        copEntity.staggerUntil = world.time.elapsed + copEntity.staggerSeconds;
        copEntity.pushResist = COP.kinds[copEntity.kind].pushResist;
      }
    }
  }
}

function checkKills(world: World): void {
  const cops = getCops();
  for (let i = ducklings.length - 1; i >= 0; i--) {
    const d = ducklings[i]!;
    for (const cop of cops) {
      if (cop.group.position.distanceTo(d.group.position) < DUCKLING.killRadius) {
        killDuckling(world, d);
        break;
      }
    }
  }
}

export const ducklingSystem: System = {
  name: 'duckling',

  fixedUpdate(dt: number, world: World) {
    const motherSrc = world.playerMode === 'driving' && world.activeVehicle
      ? world.activeVehicle.group : world.duck;
    maybeAddPathPoint(motherSrc.position.x, motherSrc.position.z, motherSrc.rotation.y);

    if (world.time.elapsed - lastThreatCheck >= 0.25) {
      lastThreatCheck = world.time.elapsed;
      reassignDefenders(world);
    }
    for (const d of ducklings) {
      if (d.mode === 'returning' && world.time.elapsed >= d.returnUntil) d.mode = 'following';
      if (d.mode === 'defending') updateDefending(d, world, dt);
      else updateFollowing(d, dt);
      // Push ducklings out of buildings (they have no physics bodies).
      const fixed = pushOutOfBuildings(d.group.position.x, d.group.position.z);
      d.group.position.x = fixed.x;
      d.group.position.z = fixed.z;
    }
    checkKills(world);

    if (ducklings.length > 0 && world.time.elapsed >= nextChirpAt) {
      playDucklingChirp(ducklings.length);
      nextChirpAt = world.time.elapsed + DUCKLING_CHIRP.baseInterval
        - ducklings.length * DUCKLING_CHIRP.intervalPerDuckling;
    }

    const isRaging = world.time.elapsed < rageUntil;
    if (wasRaging && !isRaging) exitRage(world);
    wasRaging = isRaging;
  },

  render(_dt: number, _alpha: number, world: World) {
    const t = world.time.elapsed;
    for (let i = 0; i < ducklings.length; i++) {
      const d = ducklings[i]!;
      if (d.mode !== 'following') { d.group.position.y = 0; d.group.rotation.z = 0; continue; }
      const phase = i * 1.3;
      d.group.position.y = Math.sin(t * DUCKLING_ANIM.bobFrequency + phase) * DUCKLING_ANIM.bobAmplitude;
      d.group.rotation.z = Math.sin(t * DUCKLING_ANIM.swayFrequency + phase) * DUCKLING_ANIM.waddleTilt;
    }
  },
};
